import { FieldOption, IdentityApiModel, UserType } from '@api/generated/models';
import { IdentityApiService } from '@api/identity_api.service';
import { devLocalStorage } from '@modules/dev_local_storage';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { LocalPushNotification } from '@services/notification.service/local_push_notification';
import { NotificationService } from '@services/notification.service/notification.service';
import { HttpError, isHttpError } from '@visiba/http';
import { Singleton } from '@visiba/instantiation';
import { EventEmitter } from '@visiba/std';
import { authServiceThatOriginatesFromReactOnly } from './temp_migration_file';

@Singleton()
export class AuthService {
	public readonly events = new EventEmitter<{
		onSuccessfulAuthentication: ReadonlyUser;
		onAuthenticationTermination: ReadonlyUser;
	}>();
	private readonly interceptors: Interceptor[] = [];
	private user: User | null = null;

	constructor(
		private readonly identityApiService: IdentityApiService,
		private readonly notificationService: NotificationService,
	) {
		// Empty
	}

	public getUser(): ReadonlyUser | null {
		return this.user;
	}

	public isAuthenticated(): boolean {
		return this.getUser() != null;
	}

	/**
	 * Creates a "subscription" to the event emitter's
	 * "onSuccessfulAuthentication" and "onAuthenticationTermination" events. It's
	 * use case is to provide a way to check the authentication status if time is
	 * an concern. For instance: in a controller that's expected a certain state
	 * when the user is authenticated, you may want to set the state with second
	 * value on initialization and use the subscriber to eventually patch the
	 * state when the authentication status changes.
	 */
	public provideSubscription(): [subscriber: Subscriber, user: ReadonlyUser | null] {
		const subscriber: Subscriber = (fn) => {
			const handleSubscriberEvent = (_: ReadonlyUser | null): void => {
				fn(this.getUser());
			};

			this.events.on('onSuccessfulAuthentication', handleSubscriberEvent);
			this.events.on('onAuthenticationTermination', handleSubscriberEvent);

			return () => {
				this.events.off('onSuccessfulAuthentication', handleSubscriberEvent);
				this.events.off('onAuthenticationTermination', handleSubscriberEvent);
			};
		};

		return [subscriber, this.getUser()];
	}

	/**
	 * Fetches the user's credentials, singing them in.
	 */
	public async fetchActiveIdentity(): Promise<ReadonlyUser | null> {
		try {
			const result = await authServiceThatOriginatesFromReactOnly.instance.loadIdentity();
			if (result.err) throw new Error('could not fetch user');

			const user = result.ok;

			if (user != null) {
				const interceptors = this.interceptors.filter((interceptor) => interceptor.triggerOn === 'authenticated');
				while (interceptors.length > 0) {
					const interceptor = interceptors.shift();
					if (interceptor != null) {
						await interceptor.fn(user);
					}
				}

				this.user = user;
				this.events.emit('onSuccessfulAuthentication', user);
			}

			return this.user;
		} catch (error: unknown) {
			if (isHttpError(error)) {
				this.handleError(error);
			}

			return null;
		}
	}

	/**
	 * Removes the authenticated user's stored credentials, signing them out.
	 */
	public async terminateActiveIdentity(): Promise<void> {
		try {
			if (this.user == null) return;

			await this.identityApiService.signOut();

			const interceptors = this.interceptors.filter((interceptor) => interceptor.triggerOn === 'terminated');
			while (interceptors.length > 0) {
				const interceptor = interceptors.shift();
				if (interceptor != null) {
					await interceptor.fn(this.user);
				}
			}

			devLocalStorage.removeItem('identitySession');

			const user = this.user;
			this.user = null;
			this.events.emit('onAuthenticationTermination', user);
		} catch (error: unknown) {
			if (isHttpError(error)) {
				this.handleError(error);

				return;
			}

			throw new Error('Issues with identity termination');
		}
	}

	/**
	 * Adds an interceptor to the authentication process. The interceptor is
	 * asynchronously executed when a user's credentials are being fetched.
	 */
	public addInterceptor(interceptor: Interceptor): void {
		this.interceptors.push(interceptor);
	}

	private handleError(_: HttpError<unknown>): void {
		this.notificationService.add(
			new LocalPushNotification({
				label: I18nAndL10n.translate('PW.Common.Error.Unknown'),
				type: 'toast',
				variant: 'danger',
			}),
		);
	}

	private getUserCommonName(firstName: string | null, lastName: string | null): string {
		if (firstName && lastName) return firstName + ' ' + lastName;
		if (!firstName && lastName) return lastName;
		if (firstName && !lastName) return firstName;

		return I18nAndL10n.translate('PW.Common.Anonymous');
	}

	private parseUserApiModel(response: IdentityApiModel): ReadonlyUser {
		const firstName =
			response.FirstName == null && response.LastName == null ? I18nAndL10n.translate('PW.Common.Anonymous') : response.FirstName;
		const commonName = this.getUserCommonName(response.FirstName, response.LastName);

		return {
			id: response.Id,
			licenceHolderId: response.LicenceHolderId,
			userType: response.UserType,
			firstName: firstName ?? '',
			lastName: response.LastName ?? '',
			commonName: commonName,
			email: response.Email,
			nationality: response.Nationality,
			phone: response.Phone,
			nationalIdentityNumber: response.NationalIdentityNumber,
			phoneRequirement: response.PhoneRequirement,
			emailRequirement: response.EmailRequirement,
			nationalIdentityNumberRequirement: response.NationalIdentityNumberRequirement,
		};
	}
}

export type Disposer = () => void;
export type Subscriber = (fn: (userRef: ReadonlyUser | null) => void) => Disposer;

interface User {
	id: number;
	licenceHolderId: number;
	userType: UserType;
	firstName: string;
	lastName: string;
	commonName: string;
	phone: string | null;
	email: string | null;
	nationality: string | null;
	nationalIdentityNumber: string | null;
	emailRequirement: FieldOption;
	phoneRequirement: FieldOption;
	nationalIdentityNumberRequirement: FieldOption;
}

export type ReadonlyUser = Readonly<User>;

type Interceptor =
	| {
			triggerOn: 'authenticated';
			fn(userRef: ReadonlyUser | null): Promise<void>;
	  }
	| {
			triggerOn: 'terminated';
			fn(userRef: ReadonlyUser): Promise<void>;
	  };
